// Did the jobs run? — `job_runs`, newest first.
//
// Render's cron history is not queryable from the application, so this table is the only place
// the question can be answered without reading Render's logs. A non-zero exit is unmissable:
// the row carries a red edge as well as a pill, because a column of mostly-zeroes is exactly
// the shape of a thing people stop reading.
//
// The outcome column is the job's *own* dataclass, serialised. It is shown as key=value pairs
// rather than as a shape this component knows, because every job's is different and inventing
// a common one here would mean the table and the log line could disagree.

import { useState } from 'react';
import type { JobRunRow, JobRunsPayload } from '../api/types';
import { formatCount } from '../lib/format';
import { StatePill } from './AdminSection';
import {
  RUN_STATE_LABEL, RUN_STATE_TONE, formatDuration, formatStamp, runState,
} from './format.ts';

// Counts, and never the nested lists. `failed_owners` and `picks` belong in the expanded
// payload; on the row they would be a paragraph inside a table cell.
function outcomeSummary(outcome: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(outcome)) {
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'string') {
      parts.push(`${key}=${value}`);
    }
  }
  return parts.join(' · ');
}

function RunRow({ run }: { run: JobRunRow }) {
  const [open, setOpen] = useState(false);
  const state = runState(run);
  const summary = outcomeSummary(run.outcome);

  return (
    <>
      <tr className={'acct runrow ' + state}>
        <td>
          <button type="button" className="rowbtn" onClick={() => setOpen(!open)}>
            <span className="acctname">{run.job_name}</span>
            <div className="sm">{open ? 'Hide the outcome' : 'Show the outcome'}</div>
          </button>
        </td>
        <td>{formatStamp(run.started_at)}</td>
        <td className="num">{formatDuration(run.duration_seconds)}</td>
        <td><StatePill tone={RUN_STATE_TONE[state]} label={RUN_STATE_LABEL[state]} /></td>
        <td className="num">
          {run.failed_count > 0
            ? <b className="qflag">{formatCount(run.failed_count)}</b>
            : <span className="zero">0</span>}
        </td>
        <td><code className="mono">{run.git_commit || '—'}</code></td>
      </tr>
      {open ? (
        <tr className="detail">
          <td colSpan={6}>
            <div className="dh">Outcome</div>
            {summary ? <p className="prose">{summary}</p> : null}
            <pre className="rawjson">{JSON.stringify(run.outcome, null, 2)}</pre>
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function JobRunsTable({
  payload,
  jobName,
  onJobName,
}: {
  payload: JobRunsPayload | undefined;
  jobName: string;
  onJobName: (value: string) => void;
}) {
  const runs = payload?.runs ?? [];
  const names = payload?.job_names ?? [];

  return (
    <>
      <div className="adminfilters">
        <label className="field">
          <span className="fieldlab">Job</span>
          <select value={jobName} onChange={event => onJobName(event.target.value)}>
            <option value="">Every job</option>
            {names.map(name => <option key={name} value={name}>{name}</option>)}
          </select>
        </label>
        {payload?.truncated ? (
          <span className="admintrunc">
            Showing the newest {formatCount(payload.limit)} runs — there are more.
          </span>
        ) : null}
      </div>
      {runs.length === 0 ? (
        <p className="briefempty">
          No run has been recorded. Each of the six jobs writes a row when it starts, so an
          empty table means none of them has run since migration 013 was applied.
        </p>
      ) : (
        <div className="tablewrap">
          <table className="booktable">
            <thead>
              <tr>
                <th>Job</th>
                <th>Started</th>
                <th className="num">Took</th>
                <th>Result</th>
                <th className="num">Failed</th>
                <th>Commit</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(run => <RunRow key={run.run_id} run={run} />)}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
